'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { MONO_FONT } from '@/theme';

// Distancia al borde superior a partir de la cual un encabezado cuenta como «el actual»:
// la barra de navegación (~72px) más un margen para que cambie antes de tocar el borde.
const UMBRAL = 140;

/**
 * Índice «En este artículo». Marca la sección que se está leyendo: la última cuyo encabezado
 * ya pasó el umbral. `toc` = [{ id, content, level: 'h2' | 'h3' }].
 */
export default function ArticleToc({ toc }) {
  const [activo, setActivo] = useState(null);

  useEffect(() => {
    const ids = toc.map((i) => i.id);
    let pendiente = false;

    const calcular = () => {
      pendiente = false;
      let actual = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= UMBRAL) actual = id;
      }
      setActivo(actual);
    };
    const alScroll = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(calcular);
    };

    calcular();
    window.addEventListener('scroll', alScroll, { passive: true });
    window.addEventListener('resize', alScroll);
    return () => {
      window.removeEventListener('scroll', alScroll);
      window.removeEventListener('resize', alScroll);
    };
  }, [toc]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {toc.map((item) => {
        const esActivo = item.id === activo;
        return (
          <Box
            key={item.id}
            component="a"
            href={`#${item.id}`}
            aria-current={esActivo ? 'location' : undefined}
            sx={{
              display: 'block',
              // objetivo táctil ≥ 24px (WCAG 2.2 target-size): 4px de relleno arriba y abajo
              // reemplazan al gap de 8px entre enlaces, así el ritmo visual no cambia.
              py: '4px',
              minHeight: '24px',
              fontFamily: MONO_FONT,
              fontSize: '11px',
              lineHeight: 1.45,
              letterSpacing: '0.02em',
              color: esActivo ? 'text.accent' : 'text.muted',
              fontWeight: esActivo ? 600 : 400,
              textDecoration: 'none',
              pl: item.level === 'h3' ? '22px' : '10px',
              borderLeft: '2px solid',
              borderColor: esActivo ? 'text.accent' : 'transparent',
              transition: 'color 0.15s, border-color 0.15s',
              '&:hover': { color: 'text.accent' },
            }}
          >
            {item.content}
          </Box>
        );
      })}
    </Box>
  );
}
